import { CaptureLink } from "@cryptors/api-client";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { QRCodeSVG } from "qrcode.react";
import { FC, useState } from "react";

import { useGenerateCaptureLink } from "@/api/captureLink.api";

type CaptureLinkDialogProps = {
  open: boolean;
  onClose: () => void;
  cryptorId: number;
};

const defaultExpiration = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d;
};

const CaptureLinkDialog: FC<CaptureLinkDialogProps> = ({
  open,
  onClose,
  cryptorId,
}) => {
  const [expiresAt, setExpiresAt] = useState<Date | null>(defaultExpiration());
  const [link, setLink] = useState<CaptureLink | null>(null);
  const [copied, setCopied] = useState(false);
  const { mutateAsync, isPending, isError } = useGenerateCaptureLink();

  const handleClose = () => {
    setLink(null);
    setCopied(false);
    setExpiresAt(defaultExpiration());
    onClose();
  };

  const handleGenerate = async () => {
    if (!expiresAt) return;
    const result = await mutateAsync({
      id: cryptorId,
      expiresAt: expiresAt.toISOString(),
    });
    setLink(result);
    setCopied(false);
  };

  const handleCopy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link.url);
    setCopied(true);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Générer un lien de capture</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            Ce lien permet de capturer ce cryptor directement (sans respecter le
            rayon de géolocalisation ni le statut brouillon), jusqu'à la date
            d'expiration choisie.
          </Typography>
          <DateTimePicker
            label="Expiration"
            value={expiresAt}
            onChange={setExpiresAt}
            disablePast
            format="dd/MM/yyyy HH:mm"
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          {isError && (
            <Alert severity="error">
              Impossible de générer le lien. Vérifiez la date d'expiration.
            </Alert>
          )}
          {link && (
            <Stack gap={2} alignItems="center">
              <Box
                sx={{ p: 2, bgcolor: "#fff", borderRadius: 1 }}
                display="flex"
                justifyContent="center"
              >
                <QRCodeSVG value={link.url} size={200} />
              </Box>
              <TextField
                label="Lien"
                value={link.url}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopy} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {copied && (
                <Typography variant="caption" color="success.main">
                  Lien copié dans le presse-papier
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Expire le {new Date(link.expiresAt).toLocaleString("fr-FR")}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={handleClose}>Fermer</LoadingButton>
        <LoadingButton
          onClick={handleGenerate}
          loading={isPending}
          disabled={!expiresAt}
          variant="contained"
        >
          {link ? "Régénérer" : "Générer"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CaptureLinkDialog;
